<script setup lang="ts">
import { computed, ref } from 'vue';

import FieldAwareRuleInput from './FieldAwareRuleInput.vue';
import { IconAdd, IconChevronDown, IconClose, IconReorder } from '@/components/design/icons';
import type {
  AndCriterionInput,
  OrCriterionInput,
  NotCriterionInput,
  EqualsCriterion,
  ContainsCriterion,
  GreaterThanCriterion,
  GreaterEqualThanCriterion,
  LessThanCriterion,
  LessEqualThanCriterion,
  CurrencyAmountCriterion,
  EntityCriterion,
  CalendarEventCriterion,
  ContractualPartyCriterion,
  MatterCriterion,
  OwnerCriterion,
  DateCriterion,
  ListAnyCriterion,
  ListAllCriterion,
  EnumResponseType,
} from '@/api';
import { isEnumValue } from '@/utils/enumUtils';
import {
  isAndCriterion,
  isOrCriterion,
  isNotCriterion,
  isEqualsCriterion,
  isContainsCriterion,
  isGreaterThanCriterion,
  isGreaterEqualThanCriterion,
  isLessThanCriterion,
  isLessEqualThanCriterion,
  isCurrencyAmountCriterion,
  isEntityCriterion,
  isCalendarEventCriterion,
  isStaticColumnCriterion,
  isListAnyCriterion,
  isListAllCriterion,
  isListCriterion,
  getObjectCriterionState,
  type StaticCriterionColumnType,
} from '@/utils/contractManagementView/staticFilters';
import {
  CRITERION_TYPES,
  getCriterionType,
  isStaticColumnType,
  isSpecialCriterionType,
  isListOperatorType,
  type CriterionTypeValue,
  type CriterionItem,
} from '@/utils/contractManagementView/criterionConstants';
import {
  type ResponseType,
  validateFilterValue,
  getPlaceholder,
  getInputType,
  getAvailableFilterOperations,
  getElementType,
} from '@/utils/contractManagementView/filterValueValidation';
import { datetimeStringToTimestamp, timestampToDatetimeString } from '@/utils/datetimeUtils';
import QueryGroup from './QueryGroup.vue';
import GaiaAddButton from '@/components/design/base/GaiaAddButton.vue';
import GaiaButton from '@/components/design/base/GaiaButton.vue';
import GaiaDeleteButton from '@/components/design/base/GaiaDeleteButton.vue';
import GaiaTag from '@/components/design/base/GaiaTag.vue';
import GaiaText from '@/components/design/base/GaiaText.vue';
import { gaiaAccent, gaiaNeutral, gaiaShadow, gaiaStatus } from '@/components/design/themes/tokens';
import GaiaSelect from '@/components/design/base/selects/GaiaSelect.vue';
import GaiaSwitch from '@/components/design/base/GaiaSwitch.vue';
import GaiaDatePicker from '@/components/design/base/inputs/GaiaDatePicker.vue';
import GaiaCheckbox from '@/components/design/base/GaiaCheckbox.vue';
import GaiaInput from '@/components/design/base/inputs/GaiaInput.vue';

interface DragState {
  sourcePath: number[];
  sourceCriterion: unknown;
  type: 'rule' | 'group';
}

interface Props {
  criterion: AndCriterionInput | OrCriterionInput | null;
  path: number[];
  depth: number;
  availableOptions: { label: string; value: string | number }[];
  placeholder: string;
  dragState: DragState | null;
  dragOverGroupPath: number[] | null;
  responseType?: ResponseType;
  enumResponseType?: EnumResponseType;
  isStaticColumn?: boolean;
  staticColumnType?: StaticCriterionColumnType;
  /** Offer "No date" on Equals date rules (`== null`: the row has no such date). */
  allowEmptyDate?: boolean;
}

interface Emits {
  (e: 'drag-start', event: DragEvent, criterion: unknown, path: number[]): void;
  (e: 'drag-end', event?: DragEvent): void;
  (e: 'drag-over', event: DragEvent, path: number[]): void;
  (e: 'drop', event: DragEvent, path: number[]): void;
  (e: 'add-rule', path: number[], ruleType: CriterionTypeValue): void;
  (e: 'add-group', path: number[], type: CRITERION_TYPES.AND | CRITERION_TYPES.OR): void;
  (e: 'remove-item', path: number[]): void;
  (e: 'toggle-operator', path: number[]): void;
  (e: 'toggle-rule-not', path: number[]): void;
  (e: 'update-rule-value', path: number[], value: unknown): void;
  (
    e: 'update-object-field',
    path: number[],
    field: string,
    operator: CriterionTypeValue,
    value: string | number
  ): void;
  (e: 'change-rule-type', path: number[], newType: CriterionTypeValue): void;
  (e: 'validation-error', path: number[], hasError: boolean, error?: string): void;
}

const props = withDefaults(defineProps<Props>(), {
  isStaticColumn: true,
  responseType: undefined,
  enumResponseType: undefined,
  staticColumnType: undefined,
  allowEmptyDate: false,
});

const emit = defineEmits<Emits>();

/** A boolean rule's value, as the two strings the query model stores. */
const YES_NO_OPTIONS = [
  { label: 'Yes', value: 'true' },
  { label: 'No', value: 'false' },
];

// Track validation errors for each rule
const validationErrors = ref<Map<string, string>>(new Map());

// Label mapping for different criterion types
const RULE_LABELS: Record<string, string> = {
  [CRITERION_TYPES.EQUALS]: 'Equals',
  [CRITERION_TYPES.CONTAINS]: 'Contains',
  [CRITERION_TYPES.GREATER_THAN]: 'Greater Than',
  [CRITERION_TYPES.GREATER_EQUAL]: 'Greater or Equal',
  [CRITERION_TYPES.LESS_THAN]: 'Less Than',
  [CRITERION_TYPES.LESS_EQUAL]: 'Less or Equal',
  [CRITERION_TYPES.CURRENCY_AMOUNT]: 'Currency Amount',
  [CRITERION_TYPES.ENTITY]: 'Entity',
  [CRITERION_TYPES.CALENDAR_EVENT]: 'Calendar Event',
  [CRITERION_TYPES.LIST_ANY]: 'Match Any',
  [CRITERION_TYPES.LIST_ALL]: 'Match All',
};

// Map filter operation strings to criterion types (used in multiple places)
const OPERATION_TO_TYPE_MAP: Record<string, CriterionTypeValue> = {
  equals: CRITERION_TYPES.EQUALS,
  contains: CRITERION_TYPES.CONTAINS,
  greater_than: CRITERION_TYPES.GREATER_THAN,
  greater_equal: CRITERION_TYPES.GREATER_EQUAL,
  less_than: CRITERION_TYPES.LESS_THAN,
  less_equal: CRITERION_TYPES.LESS_EQUAL,
  currency_amount: CRITERION_TYPES.CURRENCY_AMOUNT,
  entity: CRITERION_TYPES.ENTITY,
  calendar_event: CRITERION_TYPES.CALENDAR_EVENT,
  list_any: CRITERION_TYPES.LIST_ANY,
  list_all: CRITERION_TYPES.LIST_ALL,
};

// Comparison operators for inner rule dropdowns
// Excludes special types (currency_amount, entity, calendar_event) as they are auto-selected
// and shown with field-aware UI instead of type selector
// For multiple choice enums, only show 'equals' as the inner operation
const innerOperatorOptions = computed(() => {
  // For multiple choice enums, inner operation is always 'equals'
  if (props.enumResponseType?.multiple_choices === true) {
    return [
      {
        label: RULE_LABELS[CRITERION_TYPES.EQUALS],
        key: CRITERION_TYPES.EQUALS,
      },
    ];
  }

  if (!props.responseType) return [];

  const elementType = getElementType(props.responseType);
  const availableOps = getAvailableFilterOperations(elementType, undefined);

  return availableOps
    .filter(op => {
      const type = OPERATION_TO_TYPE_MAP[op];
      // Filter out special types - they are auto-selected and use field-aware UI
      return (
        type !== CRITERION_TYPES.CURRENCY_AMOUNT &&
        type !== CRITERION_TYPES.ENTITY &&
        type !== CRITERION_TYPES.CALENDAR_EVENT
      );
    })
    .map(op => ({
      label: RULE_LABELS[OPERATION_TO_TYPE_MAP[op]],
      key: OPERATION_TO_TYPE_MAP[op],
    }));
});

// Groups of operators for UI logic
// Dropdown options for Add button - nested structure for cascading menu
// Generate rule options based on available filter operations for the response type
const addDropdownOptions = computed(() => {
  const ruleOptions: {
    label: string;
    key: string;
    props: { onClick: () => void };
  }[] = [];

  // Only show rule options for non-static columns (static columns have their own UI)
  if (!props.isStaticColumn && (props.responseType || props.enumResponseType)) {
    const availableOperations = getAvailableFilterOperations(
      props.responseType,
      props.enumResponseType
    );

    for (const operation of availableOperations) {
      const type = OPERATION_TO_TYPE_MAP[operation];
      ruleOptions.push({
        label: RULE_LABELS[type] || operation,
        key: `rule-${operation}`,
        props: {
          onClick: (): void => {
            emit('add-rule', props.path, type);
          },
        },
      });
    }
  } else if (props.isStaticColumn) {
    // Static columns: show Equals and Contains
    for (const type of [CRITERION_TYPES.EQUALS, CRITERION_TYPES.CONTAINS]) {
      ruleOptions.push({
        label: RULE_LABELS[type],
        key: `rule-${type === CRITERION_TYPES.EQUALS ? 'equals' : 'contains'}`,
        props: {
          onClick: (): void => {
            emit('add-rule', props.path, type);
          },
        },
      });
    }
  }

  return [
    {
      label: 'Rule',
      key: 'rule',
      children: ruleOptions,
    },
    {
      label: 'Logic',
      key: 'logic',
      children: [
        {
          label: 'AND',
          key: 'logic-and',
          props: {
            onClick: (): void => {
              emit('add-group', props.path, CRITERION_TYPES.AND);
            },
          },
        },
        {
          label: 'OR',
          key: 'logic-or',
          props: {
            onClick: (): void => {
              emit('add-group', props.path, CRITERION_TYPES.OR);
            },
          },
        },
      ],
    },
  ];
});

const operator = computed(() => {
  if (isAndCriterion(props.criterion)) return 'AND';
  if (isOrCriterion(props.criterion)) return 'OR';
  return 'AND';
});
// Track which rule's dropdown is open (by path string)
const openRuleTypeDropdown = ref<string | null>(null);

// Get available rule type options for switching (based on response type)
function getRuleTypeSwitchOptions(
  currentType: CriterionTypeValue
): { label: string; key: string }[] {
  const options: { label: string; key: string }[] = [];

  // For non-static columns, show all available operations
  if (!props.isStaticColumn && (props.responseType || props.enumResponseType)) {
    const availableOperations = getAvailableFilterOperations(
      props.responseType,
      props.enumResponseType
    );

    for (const operation of availableOperations) {
      const type = OPERATION_TO_TYPE_MAP[operation];
      if (type !== currentType) {
        // Use type directly as key
        options.push({
          label: RULE_LABELS[type] || operation,
          key: type,
        });
      }
    }
  } else if (props.isStaticColumn) {
    // Static columns: show Equals and Contains
    for (const type of [CRITERION_TYPES.EQUALS, CRITERION_TYPES.CONTAINS]) {
      if (type !== currentType) {
        options.push({
          label: RULE_LABELS[type],
          key: type,
        });
      }
    }
  }

  return options;
}

// Handle rule type dropdown selection
function handleRuleTypeSelect(newType: string, path: number[]): void {
  if (isEnumValue(CRITERION_TYPES, newType)) emit('change-rule-type', path, newType);
  openRuleTypeDropdown.value = null;
}

// Handle inner rule type selection for list criteria
function handleInnerRuleTypeSelect(key: string, path: number[]): void {
  if (isEnumValue(CRITERION_TYPES, key)) emit('change-rule-type', path, key);
}

const criteria = computed(() => {
  if (!props.criterion) return [];
  if ('criteria' in props.criterion) {
    return props.criterion.criteria;
  }
  return [];
});

// Check if a criterion is a simple rule (not a group)
function isSimpleRule(criterion: unknown): boolean {
  return (
    isEqualsCriterion(criterion) ||
    isContainsCriterion(criterion) ||
    isGreaterThanCriterion(criterion) ||
    isGreaterEqualThanCriterion(criterion) ||
    isLessThanCriterion(criterion) ||
    isLessEqualThanCriterion(criterion) ||
    isCurrencyAmountCriterion(criterion) ||
    isEntityCriterion(criterion) ||
    isCalendarEventCriterion(criterion) ||
    isStaticColumnCriterion(criterion) ||
    isListCriterion(criterion)
  );
}

function notSwitchRailStyle({ checked }: { checked: boolean }): { backgroundColor: string } {
  return { backgroundColor: checked ? '#dc2626' : '#e5e7eb' };
}

// Check if an item is a NOT-wrapped rule
function isNotWrappedRule(item: unknown): boolean {
  if (!isNotCriterion(item)) return false;
  const inner = (item as NotCriterionInput).criterion;
  return isSimpleRule(inner);
}

// Get the inner rule from a NOT-wrapped rule, or return the item itself
// For list criteria, return the list criterion itself (not its inner criterion)
function unwrapRule(
  item: unknown
):
  | EqualsCriterion
  | ContainsCriterion
  | GreaterThanCriterion
  | GreaterEqualThanCriterion
  | LessThanCriterion
  | LessEqualThanCriterion
  | CurrencyAmountCriterion
  | EntityCriterion
  | CalendarEventCriterion
  | ContractualPartyCriterion
  | MatterCriterion
  | OwnerCriterion
  | DateCriterion
  | ListAnyCriterion
  | ListAllCriterion
  | null {
  if (isNotWrappedRule(item)) {
    const inner = (item as NotCriterionInput).criterion;
    if (
      isEqualsCriterion(inner) ||
      isContainsCriterion(inner) ||
      isGreaterThanCriterion(inner) ||
      isGreaterEqualThanCriterion(inner) ||
      isLessThanCriterion(inner) ||
      isLessEqualThanCriterion(inner) ||
      isCurrencyAmountCriterion(inner) ||
      isEntityCriterion(inner) ||
      isCalendarEventCriterion(inner) ||
      isStaticColumnCriterion(inner) ||
      isListAnyCriterion(inner) ||
      isListAllCriterion(inner)
    ) {
      return inner;
    }
    return null;
  }
  if (
    isEqualsCriterion(item) ||
    isContainsCriterion(item) ||
    isGreaterThanCriterion(item) ||
    isGreaterEqualThanCriterion(item) ||
    isLessThanCriterion(item) ||
    isLessEqualThanCriterion(item) ||
    isCurrencyAmountCriterion(item) ||
    isEntityCriterion(item) ||
    isCalendarEventCriterion(item) ||
    isStaticColumnCriterion(item) ||
    isListAnyCriterion(item) ||
    isListAllCriterion(item)
  ) {
    return item;
  }
  return null;
}

// Check if a rule is field-aware (static column or special complex type)
function isFieldAwareRule(rule: CriterionItem): boolean {
  const type = getCriterionType(rule);
  return isStaticColumnType(type) || isSpecialCriterionType(type);
}

// Check if a list criterion has a special type inner criterion
function isListWithSpecialTypeInner(rule: CriterionItem): boolean {
  if (!isListCriterion(rule)) return false;
  const inner = rule.criterion;
  return (
    isCurrencyAmountCriterion(inner) || isEntityCriterion(inner) || isCalendarEventCriterion(inner)
  );
}

// Get the inner criterion from a list criterion
function getListInnerCriterion(
  rule: ListAnyCriterion | ListAllCriterion
): CurrencyAmountCriterion | EntityCriterion | CalendarEventCriterion | null {
  const inner = rule.criterion;
  if (
    isCurrencyAmountCriterion(inner) ||
    isEntityCriterion(inner) ||
    isCalendarEventCriterion(inner)
  ) {
    return inner;
  }
  return null;
}

// Get current state of an object-based rule
function getObjectRuleState(rule: CriterionItem): {
  field: string;
  operator: CriterionTypeValue;
  value: string | number;
} | null {
  return getObjectCriterionState(rule);
}

// Check if response type is datetime
function isDatetimeType(responseType?: ResponseType): boolean {
  if (!responseType) return false;
  return getElementType(responseType) === 'datetime';
}

// Check if response type is a calendar date (no time)
function isDateType(responseType?: ResponseType): boolean {
  if (!responseType) return false;
  return getElementType(responseType) === 'date';
}

/** A rule as the template holds it, past any NOT wrapper. */
type UnwrappedRule = NonNullable<ReturnType<typeof unwrapRule>>;

/** An Equals date rule set to "No date": its value is an explicit null. */
function isNoDateRule(rule: UnwrappedRule): boolean {
  const inner = getRuleInnerCriterion(rule);
  return (
    getRuleType(inner) === CRITERION_TYPES.EQUALS &&
    'value' in inner &&
    (inner as { value: unknown }).value === null
  );
}

/** The picker's `YYYY-MM-DD` value, or null while unset / "No date". */
function dateRuleValue(rule: UnwrappedRule): string | null {
  const value = getRuleDisplayValue(rule);
  return typeof value === 'string' && value !== '' ? value : null;
}

/** "No date" bypasses handleRuleValueChange, which reads null as "cleared". */
function setNoDate(path: number[], noDate: boolean): void {
  emit('update-rule-value', path, noDate ? null : '');
}

// Check if a criterion is a simple value-based rule (can have its value updated)
function isValueBasedRule(criterion: unknown): boolean {
  return (
    isEqualsCriterion(criterion) ||
    isContainsCriterion(criterion) ||
    isGreaterThanCriterion(criterion) ||
    isGreaterEqualThanCriterion(criterion) ||
    isLessThanCriterion(criterion) ||
    isLessEqualThanCriterion(criterion)
  );
}

// Check if we have an enum response type
const hasEnumResponseType = computed(() => {
  return Boolean(props.enumResponseType);
});

// Get enum options for dropdown
const enumOptions = computed(() => {
  if (!props.enumResponseType) {
    return [];
  }
  return props.enumResponseType.values.map(value => ({
    label: value.name,
    value: value.name,
    definition: value.definition || '',
  }));
});

// Check if current rule is an equals operation on an enum type
// This works for both regular enum rules and list criteria with enum inner types
function isEnumEqualsRule(rule: ReturnType<typeof unwrapRule>): boolean {
  if (!hasEnumResponseType.value || !rule) return false;
  // For list criteria, check the inner criterion
  // This handles both regular list types and multiple choice enums (which are list types)
  if (isListCriterion(rule)) {
    const inner = rule.criterion;
    if (isValueBasedRule(inner)) {
      const innerType = getCriterionType(inner);
      return innerType === CRITERION_TYPES.EQUALS;
    }
    return false;
  }
  // For regular rules, check the rule type directly
  // Only show enum dropdown for single choice enums (not multiple choice)
  // Multiple choice enums should use list operations, so they won't reach here as regular rules
  if (props.enumResponseType?.multiple_choices === true) {
    return false; // Multiple choice enums use list operations, not direct equals
  }
  const ruleType = getRuleType(rule);
  return ruleType === CRITERION_TYPES.EQUALS;
}

// Helper to cast item to group type (avoids filter syntax in template)
function asGroupCriterion(item: unknown): AndCriterionInput | OrCriterionInput | null {
  if (isAndCriterion(item) || isOrCriterion(item)) {
    return item;
  }
  return null;
}

// Helper: Convert path array to string for comparison
function pathToString(path: number[]): string {
  return path.join('-');
}

// Check if this group is the drop target
const isGroupDragOver = computed(() => {
  if (!props.dragOverGroupPath) return false;
  return pathToString(props.path) === pathToString(props.dragOverGroupPath);
});

// Check if currently dragging
const isDragging = computed(() => {
  return props.dragState !== null;
});

function getRuleDisplayValue(
  rule:
    | EqualsCriterion
    | ContainsCriterion
    | GreaterThanCriterion
    | GreaterEqualThanCriterion
    | LessThanCriterion
    | LessEqualThanCriterion
    | CurrencyAmountCriterion
    | EntityCriterion
    | CalendarEventCriterion
    | ContractualPartyCriterion
    | MatterCriterion
    | OwnerCriterion
    | DateCriterion
    | ListAnyCriterion
    | ListAllCriterion
): string | number | (string | number)[] {
  // Handle list criteria - extract value from inner criterion
  if (isListAnyCriterion(rule) || isListAllCriterion(rule)) {
    const inner = rule.criterion;
    if (
      isEqualsCriterion(inner) ||
      isContainsCriterion(inner) ||
      isGreaterThanCriterion(inner) ||
      isGreaterEqualThanCriterion(inner) ||
      isLessThanCriterion(inner) ||
      isLessEqualThanCriterion(inner)
    ) {
      const val = inner.value;
      if (val === null) return '';
      if (typeof val === 'boolean') return String(val);
      return val;
    }
    return '';
  }
  if (isCurrencyAmountCriterion(rule)) {
    // Return amount value for the input
    const amountCrit = rule.amount;
    if (amountCrit) {
      const amountValue = amountCrit as
        | EqualsCriterion
        | ContainsCriterion
        | GreaterThanCriterion
        | GreaterEqualThanCriterion
        | LessThanCriterion
        | LessEqualThanCriterion;
      if ('value' in amountValue) {
        const val = amountValue.value;
        if (typeof val === 'boolean') return String(val);
        return val ?? '';
      }
    }
    return '';
  }
  if (isEntityCriterion(rule)) {
    // Return name value for the input
    const nameCrit = rule.name;
    if (nameCrit) {
      const nameValue = nameCrit;
      if ('value' in nameValue) {
        const val = nameValue.value;
        if (typeof val === 'boolean') return String(val);
        return val ?? '';
      }
    }
    return '';
  }
  if (isContainsCriterion(rule)) {
    const value = rule.value || '';
    if (!value) return '';

    // For non-static columns, Contains is substring matching (single string)
    // For static columns, it might be comma-separated (legacy support)
    if (!props.isStaticColumn) {
      // Non-static column: return as single string
      return value;
    }

    // Static column: handle comma-separated values (legacy support)
    if (value.includes(',')) {
      const values = value.split(',').map(v => v.trim());
      return values.map(v => {
        const option = props.availableOptions.find(opt => String(opt.value) === v);
        return option ? option.value : v;
      });
    }
    const option = props.availableOptions.find(opt => String(opt.value) === value);
    return option ? [option.value] : [value];
  }
  if (
    isEqualsCriterion(rule) ||
    isGreaterThanCriterion(rule) ||
    isGreaterEqualThanCriterion(rule) ||
    isLessThanCriterion(rule) ||
    isLessEqualThanCriterion(rule)
  ) {
    const value = rule.value;
    if (value === null) return '';
    if (typeof value === 'boolean') return String(value);
    return value;
  }
  if (
    isCurrencyAmountCriterion(rule) ||
    isEntityCriterion(rule) ||
    isCalendarEventCriterion(rule) ||
    isStaticColumnCriterion(rule)
  ) {
    const state = getObjectRuleState(rule);
    return state ? state.value : '';
  }
  return '';
}

// Get the inner criterion for list criteria, or the criterion itself for others
function getRuleInnerCriterion(
  rule:
    | EqualsCriterion
    | ContainsCriterion
    | GreaterThanCriterion
    | GreaterEqualThanCriterion
    | LessThanCriterion
    | LessEqualThanCriterion
    | CurrencyAmountCriterion
    | EntityCriterion
    | CalendarEventCriterion
    | ContractualPartyCriterion
    | MatterCriterion
    | OwnerCriterion
    | DateCriterion
    | ListAnyCriterion
    | ListAllCriterion
):
  | EqualsCriterion
  | ContainsCriterion
  | GreaterThanCriterion
  | GreaterEqualThanCriterion
  | LessThanCriterion
  | LessEqualThanCriterion
  | CurrencyAmountCriterion
  | EntityCriterion
  | CalendarEventCriterion
  | ContractualPartyCriterion
  | MatterCriterion
  | OwnerCriterion
  | DateCriterion {
  if (isListCriterion(rule)) {
    return rule.criterion;
  }
  return rule;
}

function getRuleType(
  rule:
    | EqualsCriterion
    | ContainsCriterion
    | GreaterThanCriterion
    | GreaterEqualThanCriterion
    | LessThanCriterion
    | LessEqualThanCriterion
    | CurrencyAmountCriterion
    | EntityCriterion
    | CalendarEventCriterion
    | ContractualPartyCriterion
    | MatterCriterion
    | OwnerCriterion
    | DateCriterion
    | ListAnyCriterion
    | ListAllCriterion
):
  | CRITERION_TYPES.EQUALS
  | CRITERION_TYPES.CONTAINS
  | CRITERION_TYPES.GREATER_THAN
  | CRITERION_TYPES.GREATER_EQUAL
  | CRITERION_TYPES.LESS_THAN
  | CRITERION_TYPES.LESS_EQUAL
  | CRITERION_TYPES.CURRENCY_AMOUNT
  | CRITERION_TYPES.ENTITY
  | CRITERION_TYPES.CALENDAR_EVENT
  | CRITERION_TYPES.LIST_ANY
  | CRITERION_TYPES.LIST_ALL {
  if (isListAnyCriterion(rule)) return CRITERION_TYPES.LIST_ANY;
  if (isListAllCriterion(rule)) return CRITERION_TYPES.LIST_ALL;
  if (isEqualsCriterion(rule)) return CRITERION_TYPES.EQUALS;
  if (isContainsCriterion(rule)) return CRITERION_TYPES.CONTAINS;
  if (isGreaterThanCriterion(rule)) return CRITERION_TYPES.GREATER_THAN;
  if (isGreaterEqualThanCriterion(rule)) return CRITERION_TYPES.GREATER_EQUAL;
  if (isLessThanCriterion(rule)) return CRITERION_TYPES.LESS_THAN;
  if (isLessEqualThanCriterion(rule)) return CRITERION_TYPES.LESS_EQUAL;
  if (isCurrencyAmountCriterion(rule)) return CRITERION_TYPES.CURRENCY_AMOUNT;
  if (isEntityCriterion(rule)) return CRITERION_TYPES.ENTITY;
  if (isCalendarEventCriterion(rule)) return CRITERION_TYPES.CALENDAR_EVENT;
  if (isStaticColumnCriterion(rule)) {
    // Static column criteria with CONTAINS rule type
    return CRITERION_TYPES.CONTAINS;
  }
  return CRITERION_TYPES.EQUALS;
}

// Validate rule value and update validation errors
function validateRuleValue(
  path: number[],
  value: string | number | (string | number)[] | null,
  rule: ReturnType<typeof unwrapRule>
): void {
  const pathKey = pathToString(path);

  if (!rule) {
    validationErrors.value.delete(pathKey);
    emit('validation-error', path, false);
    return;
  }

  const ruleType = getRuleType(rule);
  const isContains = ruleType === CRITERION_TYPES.CONTAINS;

  if (isContains) {
    // For Contains rules, value is a single string for substring matching
    // Contains always valid (substring matching accepts any string)
    validationErrors.value.delete(pathKey);
    emit('validation-error', path, false);
    return;
  }

  // Validate single value for Equals and comparison operators
  const singleValue = Array.isArray(value) ? value.at(0) : value;

  // Allow empty values while typing
  if (singleValue === null || singleValue === undefined || singleValue === '') {
    validationErrors.value.delete(pathKey);
    emit('validation-error', path, false);
    return;
  }

  // Validate non-empty values
  if (!props.responseType) {
    validationErrors.value.delete(pathKey);
    emit('validation-error', path, false);
    return;
  }

  const validation = validateFilterValue(singleValue, props.responseType);
  if (validation.valid) {
    validationErrors.value.delete(pathKey);
    emit('validation-error', path, false);
  } else {
    validationErrors.value.set(pathKey, validation.error || 'Invalid value');
    emit('validation-error', path, true, validation.error);
  }
}

function handleRuleValueChange(
  path: number[],
  value: string | number | (string | number)[] | null
): void {
  // Validate for non-static columns
  if (!props.isStaticColumn && props.responseType) {
    const itemIndex = criteria.value.findIndex((_, idx) => {
      const itemPath = [...props.path, idx];
      return pathToString(itemPath) === pathToString(path);
    });
    const rule = itemIndex === -1 ? null : unwrapRule(criteria.value[itemIndex]);
    validateRuleValue(path, value, rule);
  }

  // Always emit the update so user can see their input
  emit('update-rule-value', path, value ?? '');
}

function handleDragStart(event: DragEvent, criterion: unknown, path: number[]): void {
  emit('drag-start', event, criterion, path);
}

function handleDragEnd(event?: DragEvent): void {
  emit('drag-end', event);
}

function handleDragOver(event: DragEvent, path: number[]): void {
  emit('drag-over', event, path);
}

function handleDrop(event: DragEvent, path: number[]): void {
  emit('drop', event, path);
}
// Filter-builder chrome. Nesting is carried by indentation, so depth no longer
// needs a colour of its own — the old blue/green/amber/violet ladder was the
// loudest thing in the drawer. Accent marks drag targets, alert marks negation.
const groupSurface = gaiaNeutral.surface;
const groupBorder = gaiaNeutral.borderCard;
const rootBorder = gaiaNeutral.border;
const headerBorder = gaiaNeutral.borderSubtle;
const hoverSurface = gaiaNeutral.surfaceHover;
const handleHover = gaiaNeutral.surfaceHoverStrong;
const ruleSurface = gaiaNeutral.surfaceSoft;
const ruleBorder = gaiaNeutral.borderSubtle;
const ruleHover = gaiaNeutral.surfaceTint;
const dashedBorder = gaiaNeutral.borderDashedSoft;
const cardShadow = gaiaShadow.card;
const accentBase = gaiaAccent.base;
const accentWash = gaiaAccent.wash;
const accentTint = gaiaAccent.tint;
const accentBorder = gaiaAccent.border;
const alertSurface = gaiaStatus.alertSurface;
const alertBorder = gaiaStatus.alertBorder;
const alertText = gaiaStatus.alertText;
const alertTint = gaiaStatus.alertTint;
const handleColor = gaiaNeutral.textFaint;
const emptyIconColor = gaiaNeutral.textMuted;
</script>

<template>
  <div v-if="criterion" :class="['query-group', `depth-${depth}`]">
    <!-- Group Header with Drag Handle -->
    <div
      class="group-header"
      draggable="true"
      @dragstart="handleDragStart($event, criterion, path)"
      @dragend="handleDragEnd($event)"
    >
      <div class="group-header-left">
        <div class="drag-handle-group">
          <n-icon
            :component="IconReorder"
            :size="16"
            :style="{ color: handleColor, cursor: 'grab' }"
          />
        </div>
        <!-- AND is the default join, OR the deliberate one — accent marks the
             deliberate case rather than a second colour competing with it. -->
        <gaia-tag :variant="isAndCriterion(criterion) ? 'label' : 'accent'">
          {{ operator }}
        </gaia-tag>
        <gaia-button
          v-if="path.length > 0"
          text
          size="tiny"
          :style="{ marginLeft: '8px' }"
          @click.stop="emit('toggle-operator', path)"
        >
          Switch to {{ isAndCriterion(criterion) ? 'OR' : 'AND' }}
        </gaia-button>
      </div>
      <div class="group-header-right">
        <!-- Add Button with Dropdown -->
        <NDropdown
          :options="addDropdownOptions"
          trigger="click"
          placement="bottom-end"
          :animated="true"
          @click.stop
        >
          <!-- No noun: the menu offers a rule or a logic group, and names
               which on its own rows. -->
          <gaia-add-button variant="primary" @click.stop />
        </NDropdown>

        <gaia-delete-button
          intent="remove"
          subject="group"
          @click.stop="emit('remove-item', path)"
        />
      </div>
    </div>

    <!-- Group Content with Drop Zone -->
    <div
      :class="['group-content', { 'drag-over-active': isGroupDragOver && isDragging }]"
      @dragover.prevent="handleDragOver($event, path)"
      @dragenter.prevent
      @drop.prevent="handleDrop($event, path)"
    >
      <!-- Empty State with Drop Indicator -->
      <div v-if="criteria.length === 0" class="empty-group">
        <div class="empty-group-icon">
          <n-icon :component="IconAdd" :size="28" :style="{ color: emptyIconColor }" />
        </div>
        <gaia-text size="control" tone="subtle" :style="{ textAlign: 'center' }">
          <span v-if="isDragging">
            <strong>Drop here</strong> to add item to this {{ operator }} group
          </span>
          <span v-else>Empty group. Drag items here or use buttons below to add.</span>
        </gaia-text>
      </div>

      <!-- Items List -->
      <template v-else>
        <div
          v-for="(item, index) in criteria"
          :key="`${path.join('-')}-${index}`"
          class="item-wrapper"
        >
          <!-- Nested Group (AND/OR) -->
          <QueryGroup
            v-if="isAndCriterion(item) || isOrCriterion(item)"
            :criterion="asGroupCriterion(item)"
            :path="[...path, index]"
            :depth="depth + 1"
            :available-options="availableOptions"
            :placeholder="placeholder"
            :drag-state="dragState"
            :drag-over-group-path="dragOverGroupPath"
            :response-type="responseType"
            :enum-response-type="enumResponseType"
            :is-static-column="isStaticColumn"
            :static-column-type="staticColumnType"
            :allow-empty-date="allowEmptyDate"
            @drag-start="handleDragStart"
            @drag-end="handleDragEnd"
            @drag-over="handleDragOver"
            @drop="handleDrop"
            @add-rule="(p: number[], t: CriterionTypeValue) => emit('add-rule', p, t)"
            @add-group="
              (p: number[], t: CRITERION_TYPES.AND | CRITERION_TYPES.OR) => emit('add-group', p, t)
            "
            @remove-item="(p: number[]) => emit('remove-item', p)"
            @toggle-operator="(p: number[]) => emit('toggle-operator', p)"
            @toggle-rule-not="(p: number[]) => emit('toggle-rule-not', p)"
            @update-rule-value="(p: number[], v: any) => emit('update-rule-value', p, v)"
            @update-object-field="
              (p: number[], f: string, o: CriterionTypeValue, v: string | number) =>
                emit('update-object-field', p, f, o, v)
            "
            @change-rule-type="
              (p: number[], t: CriterionTypeValue) => emit('change-rule-type', p, t)
            "
            @validation-error="
              (p: number[], hasError: boolean, error?: string) =>
                emit('validation-error', p, hasError, error)
            "
          />

          <!-- Rule (can be wrapped in NOT or direct) -->
          <div
            v-else-if="isSimpleRule(item) || isListCriterion(item) || isNotWrappedRule(item)"
            class="rule-item"
            :class="{ 'not-wrapped': isNotWrappedRule(item) }"
            draggable="true"
            @dragstart.stop="handleDragStart($event, item, [...path, index])"
            @dragend.stop="handleDragEnd($event)"
          >
            <div class="rule-content">
              <!-- Drag Handle -->
              <div class="drag-handle">
                <NIcon :component="IconReorder" :size="18" style="color: #9ca3af; cursor: grab" />
              </div>

              <!-- Rule Type Badge (Clickable Dropdown for non-static columns only) -->
              <template v-if="unwrapRule(item)">
                <!-- Non-static columns: Clickable dropdown with icon -->
                <template v-if="!isFieldAwareRule(unwrapRule(item)!) && !isStaticColumn">
                  <div style="display: inline-flex; align-items: center; gap: 8px">
                    <NDropdown
                      :options="getRuleTypeSwitchOptions(getRuleType(unwrapRule(item)!))"
                      :show="openRuleTypeDropdown === pathToString([...path, index])"
                      trigger="click"
                      @clickoutside="
                        () => {
                          openRuleTypeDropdown = null;
                        }
                      "
                      @select="
                        (key: string) => {
                          handleRuleTypeSelect(key, [...path, index]);
                        }
                      "
                    >
                      <div
                        style="
                          display: inline-flex;
                          align-items: center;
                          justify-content: center;
                          gap: 4px;
                          cursor: pointer;
                        "
                        @click.stop="
                          () => {
                            const pathKey = pathToString([...path, index]);
                            openRuleTypeDropdown =
                              openRuleTypeDropdown === pathKey ? null : pathKey;
                          }
                        "
                      >
                        <NTag
                          :type="
                            isListOperatorType(getRuleType(unwrapRule(item)!)) ||
                            getRuleType(unwrapRule(item)!) !== CRITERION_TYPES.EQUALS
                              ? 'warning'
                              : 'default'
                          "
                          :class="{ 'not-highlighted': isNotWrappedRule(item) }"
                          size="small"
                          style="font-weight: 500; min-width: 70px; border: none"
                        >
                          {{ RULE_LABELS[getRuleType(unwrapRule(item)!)] || 'Rule' }}
                        </NTag>
                        <NIcon
                          :component="IconChevronDown"
                          :size="14"
                          style="opacity: 0.7; flex-shrink: 0"
                        />
                      </div>
                    </NDropdown>

                    <!-- Inner Operator for List Criteria (only show if NOT a special type) -->
                    <template
                      v-if="
                        isListCriterion(unwrapRule(item)!) &&
                        !isListWithSpecialTypeInner(unwrapRule(item)!)
                      "
                    >
                      <span style="font-size: 11px; color: #9ca3af; font-style: italic">where</span>
                      <NDropdown
                        :options="innerOperatorOptions"
                        trigger="click"
                        @select="(key: string) => handleInnerRuleTypeSelect(key, [...path, index])"
                      >
                        <div
                          style="
                            display: inline-flex;
                            align-items: center;
                            gap: 2px;
                            cursor: pointer;
                          "
                        >
                          <NTag
                            size="small"
                            :bordered="false"
                            type="info"
                            style="font-weight: 500; min-width: 40px"
                          >
                            {{
                              RULE_LABELS[getRuleType((unwrapRule(item) as any).criterion)] ||
                              (unwrapRule(item) as any).criterion.type ||
                              (unwrapRule(item) as any).criterion.criterion_type
                            }}
                          </NTag>
                          <NIcon :component="IconChevronDown" :size="12" style="opacity: 0.6" />
                        </div>
                      </NDropdown>
                    </template>
                  </div>
                </template>
                <!-- Static columns: Non-clickable tag (no dropdown, no icon) -->
                <NTag
                  v-else
                  :type="
                    getRuleType(unwrapRule(item)!) === CRITERION_TYPES.EQUALS
                      ? 'default'
                      : 'warning'
                  "
                  :class="{ 'not-highlighted': isNotWrappedRule(item) }"
                  size="small"
                  style="font-weight: 500; min-width: 70px"
                >
                  {{ RULE_LABELS[getRuleType(unwrapRule(item)!)] || 'Rule' }}
                </NTag>
              </template>

              <!-- Value Input/Selector -->
              <template v-if="unwrapRule(item)">
                <!-- List criterion with special type inner criterion: Show field-aware UI for inner -->
                <FieldAwareRuleInput
                  v-if="
                    isListCriterion(unwrapRule(item)!) &&
                    isListWithSpecialTypeInner(unwrapRule(item)!)
                  "
                  :rule="
                    getListInnerCriterion(unwrapRule(item)! as ListAnyCriterion | ListAllCriterion)!
                  "
                  :path="[...path, index]"
                  @update-object-field="
                    (p: number[], f: string, o: CriterionTypeValue, v: string | number) =>
                      emit('update-object-field', p, f, o, v)
                  "
                />

                <!-- Field-aware rule: Triple-input UI (Field, Operator, Value) -->
                <FieldAwareRuleInput
                  v-else-if="isFieldAwareRule(unwrapRule(item)!)"
                  :rule="unwrapRule(item)!"
                  :path="[...path, index]"
                  @update-object-field="
                    (p: number[], f: string, o: CriterionTypeValue, v: string | number) =>
                      emit('update-object-field', p, f, o, v)
                  "
                />

                <!-- Normal rule types (Simple types) -->
                <template v-else-if="!isStaticColumn && (responseType || enumResponseType)">
                  <!-- Enum type with equals: Use select dropdown -->
                  <!-- This handles both:
                       1. Single choice enums with direct equals operation
                       2. Multiple choice enums with list_any/list_all -> equals inner operation -->
                  <template v-if="hasEnumResponseType && isEnumEqualsRule(unwrapRule(item))">
                    <gaia-select
                      :value="String(getRuleDisplayValue(unwrapRule(item)!) ?? '')"
                      :options="enumOptions"
                      placeholder="Select value..."
                      size="small"
                      :class="{ 'not-highlighted': isNotWrappedRule(item) }"
                      :status="
                        validationErrors.get(pathToString([...path, index])) ? 'error' : undefined
                      "
                      style="flex: 1; min-width: 200px"
                      clearable
                      filterable
                      @update:value="handleRuleValueChange([...path, index], $event)"
                    />
                  </template>

                  <!-- Boolean type: Use select -->
                  <template
                    v-else-if="
                      getElementType(responseType || 'str') === 'bool' &&
                      getRuleType(getRuleInnerCriterion(unwrapRule(item)!)) ===
                        CRITERION_TYPES.EQUALS
                    "
                  >
                    <gaia-select
                      :value="String(getRuleDisplayValue(unwrapRule(item)!) ?? '')"
                      :options="YES_NO_OPTIONS"
                      placeholder="Select yes/no"
                      size="small"
                      :class="{ 'not-highlighted': isNotWrappedRule(item) }"
                      :status="
                        validationErrors.get(pathToString([...path, index])) ? 'error' : undefined
                      "
                      style="flex: 1; min-width: 200px"
                      clearable
                      @update:value="handleRuleValueChange([...path, index], $event)"
                    />
                  </template>

                  <!-- Datetime type: Use NDatePicker -->
                  <template v-else-if="isDatetimeType(responseType)">
                    <gaia-date-picker
                      :value="
                        datetimeStringToTimestamp(getRuleDisplayValue(unwrapRule(item)!) as string)
                      "
                      type="datetime"
                      placeholder="Select date and time"
                      size="small"
                      :class="{ 'not-highlighted': isNotWrappedRule(item) }"
                      :status="
                        validationErrors.get(pathToString([...path, index])) ? 'error' : undefined
                      "
                      style="flex: 1; min-width: 200px"
                      clearable
                      format="yyyy-MM-dd HH:mm:ss"
                      @update:value="
                        (val: number | null) =>
                          handleRuleValueChange([...path, index], timestampToDatetimeString(val))
                      "
                    />
                  </template>

                  <!-- Date type: date picker writing ISO YYYY-MM-DD -->
                  <template v-else-if="isDateType(responseType)">
                    <gaia-date-picker
                      :formatted-value="dateRuleValue(unwrapRule(item)!)"
                      type="date"
                      value-format="yyyy-MM-dd"
                      :placeholder="isNoDateRule(unwrapRule(item)!) ? 'No date' : 'Select date'"
                      size="small"
                      :disabled="isNoDateRule(unwrapRule(item)!)"
                      :class="{ 'not-highlighted': isNotWrappedRule(item) }"
                      :status="
                        validationErrors.get(pathToString([...path, index])) ? 'error' : undefined
                      "
                      style="flex: 1; min-width: 200px"
                      clearable
                      @update:formatted-value="
                        (val: string | null) => handleRuleValueChange([...path, index], val)
                      "
                    />
                    <gaia-checkbox
                      v-if="
                        allowEmptyDate &&
                        getRuleType(getRuleInnerCriterion(unwrapRule(item)!)) ===
                          CRITERION_TYPES.EQUALS
                      "
                      :checked="isNoDateRule(unwrapRule(item)!)"
                      size="small"
                      @update:checked="(on: boolean) => setNoDate([...path, index], on)"
                    >
                      No date
                    </gaia-checkbox>
                  </template>

                  <!-- Normal rule types -->
                  <template v-else>
                    <gaia-input
                      :value="String(getRuleDisplayValue(unwrapRule(item)!) ?? '')"
                      type="text"
                      :input-props="{ type: getInputType(responseType || 'str') }"
                      :placeholder="getPlaceholder(responseType || 'str', false)"
                      size="small"
                      :class="{ 'not-highlighted': isNotWrappedRule(item) }"
                      :status="
                        validationErrors.get(pathToString([...path, index])) ? 'error' : undefined
                      "
                      style="flex: 1; min-width: 200px"
                      clearable
                      @update:value="handleRuleValueChange([...path, index], $event)"
                    />
                  </template>

                  <!-- Error message -->
                  <NText
                    v-if="validationErrors.get([...path, index].join('-'))"
                    depth="3"
                    style="font-size: 11px; color: #ef4444; margin-top: 4px; display: block"
                  >
                    {{ validationErrors.get(pathToString([...path, index])) }}
                  </NText>
                </template>

                <!-- Select dropdown for standard static columns fallback (legacy/simpler ones) -->
                <gaia-select
                  v-else
                  :value="getRuleDisplayValue(unwrapRule(item)!)"
                  :options="availableOptions"
                  :multiple="getRuleType(unwrapRule(item)!) === CRITERION_TYPES.CONTAINS"
                  filterable
                  :placeholder="placeholder"
                  size="small"
                  :class="{ 'not-highlighted': isNotWrappedRule(item) }"
                  style="flex: 1; min-width: 200px"
                  clearable
                  @update:value="handleRuleValueChange([...path, index], $event)"
                />
              </template>

              <!-- Action Buttons -->
              <div class="rule-actions">
                <!-- Is/Not Toggle Switch -->
                <div class="is-not-toggle">
                  <NText
                    :style="{
                      fontSize: '11px',
                      fontWeight: !isNotWrappedRule(item) ? 600 : 400,
                      color: !isNotWrappedRule(item) ? '#1f2937' : '#9ca3af',
                      transition: 'all 0.2s ease',
                    }"
                  >
                    is
                  </NText>

                  <gaia-switch
                    :value="isNotWrappedRule(item)"
                    size="small"
                    :rail-style="notSwitchRailStyle"
                    @update:value="
                      () => {
                        // Toggle NOT wrapping for this specific rule
                        emit('toggle-rule-not', [...path, index]);
                      }
                    "
                    @click.stop
                  />
                  <NText
                    :style="{
                      fontSize: '11px',
                      fontWeight: isNotWrappedRule(item) ? 600 : 400,
                      color: isNotWrappedRule(item) ? '#dc2626' : '#9ca3af',
                      transition: 'all 0.2s ease',
                    }"
                  >
                    not
                  </NText>
                </div>

                <!-- Delete Button -->
                <NButton
                  text
                  size="small"
                  type="error"
                  @click.stop="emit('remove-item', [...path, index])"
                >
                  <template #icon>
                    <NIcon :component="IconClose" />
                  </template>
                </NButton>
              </div>
            </div>
          </div>
        </div>

        <!-- Drop Zone Indicator at Bottom (when dragging) -->
        <div v-if="isDragging && isGroupDragOver" class="drop-zone-indicator">
          <div class="drop-zone-line"></div>
          <NText strong style="font-size: 11px; color: #3b82f6"> Drop here to add to bottom </NText>
          <div class="drop-zone-line"></div>
        </div>
      </template>
    </div>
  </div>
</template>

<style scoped>
.query-group {
  background: v-bind(groupSurface);
  border: 1px solid v-bind(groupBorder);
  border-radius: 10px;
  padding: 12px;
  margin-bottom: 12px;
  transition: all 0.2s ease;
  position: relative;
}

/* Depth reads from the indent, not from a colour. The root keeps a slightly
   firmer edge because it is the group everything else sits inside. */
.query-group.depth-0 {
  border-color: v-bind(rootBorder);
  box-shadow: v-bind(cardShadow);
}

.query-group.depth-1 {
  margin-left: 24px;
}

.query-group.depth-2 {
  margin-left: 48px;
}

.query-group.depth-3,
.query-group.depth-4,
.query-group.depth-5 {
  margin-left: 72px;
}

.group-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 12px;
  border-bottom: 1px solid v-bind(headerBorder);
  margin-bottom: 12px;
  cursor: grab;
  border-radius: 6px;
  transition: background 0.15s ease;
  user-select: none;
}

.group-header:hover {
  background: v-bind(hoverSurface);
}

.group-header:active {
  cursor: grabbing;
}

.group-header-left {
  display: flex;
  align-items: center;
  gap: 8px;
}

.drag-handle-group {
  display: flex;
  align-items: center;
  justify-content: center;
}

.group-header-right {
  display: flex;
  align-items: center;
  gap: 8px;
}

.group-content {
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-height: 60px;
  padding: 12px;
  border-radius: 8px;
  transition: all 0.2s ease;
  position: relative;
}

.group-content.drag-over-active {
  background: v-bind(accentWash);
  border: 1px dashed v-bind(accentBorder);
}

.empty-group {
  padding: 24px;
  text-align: center;
  background: v-bind(ruleSurface);
  border: 1px dashed v-bind(dashedBorder);
  border-radius: 8px;
  min-height: 80px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  transition: all 0.2s ease;
}

.empty-group-icon {
  opacity: 0.5;
  transition: opacity 0.2s ease;
}

.drag-over-active .empty-group {
  background: v-bind(groupSurface);
  border-color: v-bind(accentBorder);
}

.drag-over-active .empty-group-icon {
  opacity: 1;
}

.item-wrapper {
  width: 100%;
}

.rule-item {
  background: v-bind(ruleSurface);
  border: 1px solid v-bind(ruleBorder);
  border-radius: 8px;
  padding: 8px;
  transition: all 0.15s ease;
  cursor: grab;
}

.rule-item:hover {
  background: v-bind(ruleHover);
  box-shadow: v-bind(cardShadow);
}

/* A negated rule is the one place colour still carries meaning. */
.rule-item.not-wrapped {
  background: v-bind(alertSurface);
  border-color: v-bind(alertBorder);
}

.rule-item.not-wrapped:hover {
  background: v-bind(alertTint);
}

.rule-item.not-wrapped .rule-content {
  background: transparent;
}

.rule-item:active {
  cursor: grabbing;
}

.rule-content {
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
}

.rule-actions {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-left: auto;
}

.is-not-toggle {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 4px 8px;
  border-radius: 6px;
  background: v-bind(ruleSurface);
  border: 1px solid v-bind(ruleBorder);
  transition: all 0.2s ease;
}

.is-not-toggle:hover {
  background: v-bind(ruleHover);
  border-color: v-bind(rootBorder);
}

/* !important stays: these fight naive's own selection colours. */
.not-highlighted {
  background: v-bind(alertTint) !important;
  border-color: v-bind(alertBorder) !important;
  color: v-bind(alertText) !important;
}

.not-highlighted :deep(.n-base-selection) {
  background: v-bind(alertTint) !important;
  border-color: v-bind(alertBorder) !important;
}

.not-highlighted :deep(.n-base-selection:hover) {
  background: v-bind(alertSurface) !important;
  border-color: v-bind(alertBorder) !important;
}

.not-highlighted :deep(.n-base-selection-label) {
  color: v-bind(alertText) !important;
}

.drag-handle {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 4px;
  cursor: grab;
  border-radius: 4px;
  transition: background 0.15s ease;
  user-select: none;
}

.drag-handle:hover {
  background: v-bind(handleHover);
}

.drag-handle:active {
  cursor: grabbing;
}

.drop-zone-indicator {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px;
  background: v-bind(accentTint);
  border: 1px dashed v-bind(accentBorder);
  border-radius: 8px;
  margin-top: 8px;
  animation: pulse-drop 1.5s ease-in-out infinite;
}

@keyframes pulse-drop {
  0%,
  100% {
    opacity: 1;
  }
  50% {
    opacity: 0.75;
  }
}

.drop-zone-line {
  flex: 1;
  height: 2px;
  background: linear-gradient(to right, transparent, v-bind(accentBase), transparent);
}

/* Dragging state */
:global(.dragging) {
  opacity: 0.4;
  box-shadow: v-bind(cardShadow);
}
</style>
